import type { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ConstructionTypeData } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { gqlWorkOrderConstructionTypes } from '../workOrderQueries';

export const useConstructionTypes = (
    workOrderId: number | undefined,
    withEffect = true,
    customRequestId?: string,
): [ConstructionTypeData[], RequestStatus | undefined, () => void] => {
    const dispatch = useDispatch();
    const variables = useMemo(() => ({ workOrderId }), [workOrderId]);

    const requestId = useMemo(
        () => customRequestId ?? `workOrderConstructionTypes${workOrderId}`,
        [customRequestId, workOrderId],
    );

    const [
        workOrderConstructionTypesResponse,
        workOrderConstructionTypesState,
        ,
        workOrderConstructionTypesMakeRequest,
    ] = useRequest({
        requestId,
        query: gqlWorkOrderConstructionTypes,
        variables,
        withEffect: withEffect && !!workOrderId,
    });

    const [reset] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            if (withEffect) {
                reset(undefined);
            }
        },
        [reset, withEffect, workOrderId],
    );

    const refreshConstructionTypes = useCallback(() => {
        dispatch(workOrderConstructionTypesMakeRequest(variables));
    }, [dispatch, variables, workOrderConstructionTypesMakeRequest]);

    return [
        (workOrderConstructionTypesResponse?.uerWorkOrderConstructionTypes as ConstructionTypeData[]) ?? [],
        workOrderConstructionTypesState,
        refreshConstructionTypes,
    ];
};
